// =============================================================================
// timeFormat — utility helpers for translating UTC hours to common
// operator-friendly time zones. Used by the "Run anonymous-user cleanup"
// schedule pickers on the super-admin Retention Defaults page and the
// tenant-admin Data Retention page.
//
// The picker stores UTC, but the operator usually thinks in local time.
// We show two common locales:
//   - US Eastern — both EDT (summer, UTC-4) and EST (winter, UTC-5) so
//     the operator sees the DST shift explicitly and can plan around it.
//     US/Canada Eastern observes DST from the second Sunday of March
//     to the first Sunday of November.
//   - India — IST is always UTC+5:30 (no DST). Half-hour offset means
//     we have to compute in minutes, not whole hours.
//
// Returns a single human-readable string suitable for sub-text under
// a <select>. Example output for utcHour = 0:
//   "8 PM EDT (7 PM EST) · 5:30 AM IST (next day)"
// =============================================================================

/** Format a 24h hour value as 12-hour clock with AM/PM. */
function fmtClock12(hour: number, min: number = 0): string {
  const h12    = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const period = hour < 12 ? 'AM' : 'PM';
  const minStr = min ? `:${String(min).padStart(2, '0')}` : '';
  return `${h12}${minStr} ${period}`;
}

/**
 * Build the sub-line shown under a UTC schedule picker. Accepts optional
 * minutes so it works for both hour-only pickers (anon-cleanup hour
 * dropdown) and hour+minute pickers (super-admin Sync Schedule which
 * supports 0/15/30/45).
 *
 * utcHour: 0..23, utcMinute: 0..59 (default 0).
 */
export function formatScheduleConversions(utcHour: number, utcMinute: number = 0): string {
  const utcTotalMin = utcHour * 60 + utcMinute;
  const DAY_MIN     = 24 * 60;

  // Helper — turn an offset-applied minute value into clock + day hint.
  const localize = (offsetMin: number) => {
    const raw     = utcTotalMin + offsetMin;
    const wrapped = ((raw % DAY_MIN) + DAY_MIN) % DAY_MIN;
    const hour    = Math.floor(wrapped / 60);
    const min     = wrapped % 60;
    const hint    = raw < 0 ? ' (prev day)' : raw >= DAY_MIN ? ' (next day)' : '';
    return { hour, min, hint };
  };

  const edt = localize(-4 * 60);          // EDT — summer, UTC-4
  const est = localize(-5 * 60);          // EST — winter, UTC-5
  const ist = localize(5 * 60 + 30);      // IST — UTC+5:30 (no DST)

  // EDT and EST usually share the same day hint, but not always (e.g.
  // 4:00 UTC → 12:00 AM EDT same day vs 11:00 PM EST prev day).
  // Show the hint once when they agree; split it if they diverge.
  const usSameDay = edt.hint === est.hint;
  const usHint    = usSameDay ? edt.hint : ` (EDT${edt.hint || ' same day'} / EST${est.hint || ' same day'})`;

  return `${fmtClock12(edt.hour, edt.min)} EDT (${fmtClock12(est.hour, est.min)} EST)${usSameDay ? edt.hint : ''} · ${fmtClock12(ist.hour, ist.min)} IST${ist.hint}${!usSameDay ? usHint : ''}`;
}
